import React, {useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {Box, Button, GlobalStyles, Typography} from '@mui/material';
import {keyframes} from '@mui/material/styles';
import GalanoRegular from '../../../assets/fonts/cpc/GalanoGrotesque-Regular.otf';
import GalanoBold from '../../../assets/fonts/cpc/GalanoGrotesque-Bold.otf';
import CpcHeader from '../components/CpcHeader';
import CpcFooter from '../components/CpcFooter';
import {isCpcGovLoggedIn, landingRouteAfterLogin} from '../../../services/cpcGov.service';

/* Visual theme from https://cpc.gcforum.org/global_cpc — styling only. */
const C = {
    // navy/navyDeep/white stay literal — see the matching comment in
    // NationalOverview.page.jsx. Plain text/background roles use the
    // dedicated keys below, which flip via --cpc-* (see darkMode.css).
    navy: '#003C69',
    navyDeep: '#003D69',
    navyDark: '#002855',
    cyan: '#0096C8',
    cyanDeep: '#007DB4',
    cyanHover: '#0086b4',
    violet: '#8269D2',
    white: '#FFFFFF',
    mist: 'var(--cpc-surface-subtle, #F5F8FB)',
    muted: 'var(--cpc-text-muted, rgba(0,60,105,.72))',
    surface: 'var(--cpc-surface, #FFFFFF)',
    text: 'var(--cpc-text, #003C69)',
};

const fontFaceStyles = {
    '@font-face': [
        {fontFamily: 'GalanoGrotesque', fontWeight: 400, src: `url(${GalanoRegular}) format("opentype")`, fontDisplay: 'swap'},
        {fontFamily: 'GalanoGrotesque', fontWeight: 700, src: `url(${GalanoBold}) format("opentype")`, fontDisplay: 'swap'},
    ],
};

const cpcRise = keyframes`
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: none; }
`;

const ctaButtonSx = (bg, border, hoverBg) => ({
    border: `2px solid ${border}`,
    cursor: 'pointer',
    background: bg,
    color: C.white,
    fontFamily: 'inherit',
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.2,
    padding: '18px 42px',
    borderRadius: 0,
    textTransform: 'none',
    boxShadow: '0 4px 6px rgba(0,0,0,.1)',
    transition: 'background .2s ease, border-color .2s ease',
    '&:hover': {background: hoverBg, borderColor: hoverBg},
    '@media (max-width: 720px)': {
        fontSize: 15,
        padding: '14px 22px',
        width: '100%',
    },
});

const chipSx = (bg, fg) => ({
    fontSize: 14,
    fontWeight: 700,
    padding: '10px 14px',
    color: fg,
    background: bg,
});

const badgeSx = (bg) => ({
    alignSelf: 'flex-start',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    padding: '8px 14px',
    color: C.white,
    background: bg,
    mb: 2.5,
});

const LandingPage = () => {
    const history = useHistory();

    useEffect(() => {
        document.title = 'National CPC Index';
    }, []);

    /**
     * Both CTAs land on sign-in, not the terms.
     *
     * The terms are an attestation tied to a named reviewer and a country, so
     * they have to be accepted by an identified account — the sign-in step comes
     * first and the terms follow it. Login then forwards to the terms, or
     * straight to the dashboard if this reviewer has already accepted them
     * (see landingRouteAfterLogin).
     *
     * If the user is already signed in (e.g. they navigated to this About page
     * from the dashboard sidebar), skip the login screen entirely and send them
     * straight to where they belong.
     */
    const goToSignIn = () => {
        if (isCpcGovLoggedIn()) {
            // `fromAbout` — the reviewer is already ON this page, so an
            // un-attested one must go FORWARD to the terms rather than be handed
            // back the page they are looking at (PBDQ-182).
            landingRouteAfterLogin({fromAbout: true}).then((route) => history.push(route));
        } else {
            history.push('/dq_pro/cpc-government-dashboard/login');
        }
    };

    return (
        <Box
            className="cpc-gov-scope"
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: 'GalanoGrotesque, "Helvetica Neue", Helvetica, Arial, sans-serif',
                color: C.text,
                background: C.surface,
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
            }}
        >
            <GlobalStyles styles={fontFaceStyles}/>

            <CpcHeader
                right={(
                    <Box
                        sx={{
                            flexShrink: 0,
                            fontSize: 13,
                            fontWeight: 700,
                            color: C.white,
                            background: C.cyan,
                            padding: '10px 18px',
                            whiteSpace: 'nowrap',
                            '@media (max-width: 720px)': {
                                fontSize: 11,
                                padding: '8px 12px',
                                width: '100%',
                                textAlign: 'center',
                            },
                        }}
                    >
                        Restricted · Government access
                    </Box>
                )}
            />

            <Box component="main" sx={{flex: 1}}>
            <Box
                component="section"
                aria-label="About"
                sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    minHeight: 640,
                    '@media (max-width: 900px)': {flexDirection: 'column', minHeight: 'auto'},
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        flex: '2.2 1 0%',
                        background: C.navy,
                        color: C.white,
                        padding: '96px 64px',
                        animation: `${cpcRise} .7s ease both`,
                        '@media (max-width: 900px)': {padding: '64px 24px 40px', flex: 'none'},
                    }}
                >
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            right: '-140px',
                            bottom: '-160px',
                            width: 460,
                            height: 460,
                            borderRadius: '50%',
                            border: `2px solid ${C.cyan}`,
                            opacity: 0.16,
                        }}
                    />
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            right: '-60px',
                            bottom: '-80px',
                            width: 300,
                            height: 300,
                            borderRadius: '50%',
                            border: `2px solid ${C.cyan}`,
                            opacity: 0.22,
                        }}
                    />
                    <Box sx={{position: 'relative', zIndex: 1}}>
                        <Typography
                            component="h1"
                            sx={{
                                margin: 0,
                                maxWidth: 620,
                                fontSize: '3.8rem',
                                lineHeight: 1.2,
                                fontWeight: 700,
                                letterSpacing: 'normal',
                                textTransform: 'uppercase',
                                color: C.white,
                                '@media (max-width: 1100px)': {fontSize: '2.75rem'},
                                '@media (max-width: 720px)': {fontSize: 30},
                            }}
                        >
                            Government Checklist for Child Online Safety
                        </Typography>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        flex: '2 1 0%',
                        background: C.surface,
                        padding: '96px 96px',
                        animation: `${cpcRise} .7s ease both`,
                        '@media (max-width: 900px)': {padding: '40px 24px 64px', flex: 'none'},
                    }}
                >
                    <Box sx={{maxWidth: 640}}>
                        <Typography
                            component="p"
                            sx={{
                                margin: '0 0 36px',
                                fontSize: 25,
                                lineHeight: 1.5,
                                fontWeight: 400,
                                color: C.text,
                                '& strong, & b': {fontWeight: 700},
                                '@media (max-width: 720px)': {fontSize: 17},
                            }}
                        >
                            This Government Checklist for child online safety assesses the national systems governments establish to protect and empower children online. It provides a standardized view of system strengths, implementation gaps, and priority actions across families and communities, schools, companies, technology infrastructure, and government.
                        </Typography>
                        <Button type="button" onClick={goToSignIn} sx={ctaButtonSx(C.cyan, C.cyan, C.cyanHover)}>
                            Continue to review terms →
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Box
                component="section"
                sx={{position: 'relative', background: C.mist, borderBottom: '1px solid var(--cpc-border, rgba(0,60,105,.08))'}}
            >
                <Box aria-hidden sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: 10, background: C.cyan}}/>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 1280,
                        margin: '0 auto',
                        padding: '64px 48px',
                        boxSizing: 'border-box',
                        textAlign: 'center',
                        '@media (max-width: 900px)': {padding: '40px 24px'},
                    }}
                >
                    <Typography
                        component="p"
                        sx={{
                            margin: '0 auto',
                            maxWidth: 900,
                            fontSize: 25,
                            lineHeight: 1.3,
                            fontWeight: 600,
                            color: C.text,
                            textAlign: 'center',
                            '@media (max-width: 720px)': {fontSize: 17},
                        }}
                    >
                        The National CPC Index Toolkit combines two complementary assessment tools to provide one national picture of child online safety.
                    </Typography>
                </Box>
            </Box>

            <Box
                component="section"
                id="cpc-toolkits"
                aria-label="National CPC Index Toolkits"
                sx={{
                    width: '100%',
                    maxWidth: 1280,
                    margin: '0 auto',
                    padding: '88px 48px 0',
                    animation: `${cpcRise} .65s ease both`,
                    '@media (max-width: 900px)': {padding: '56px 24px 0'},
                }}
            >
                <Typography
                    sx={{fontSize: 14, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.cyanDeep, mb: 1.75}}
                >
                    National CPC Index Toolkits
                </Typography>
                <Typography
                    component="h2"
                    sx={{
                        margin: '0 0 18px',
                        fontSize: 45,
                        fontWeight: 700,
                        lineHeight: 1.15,
                        color: C.text,
                        maxWidth: '90%',
                        '@media (max-width: 900px)': {fontSize: 28, maxWidth: '100%'},
                        '@media (max-width: 720px)': {fontSize: 23},
                    }}
                >
                    100 Checklist Indicators. One National Picture.
                </Typography>
                <Typography
                    component="p"
                    sx={{
                        margin: '0 0 40px',
                        maxWidth: 900,
                        fontSize: 22,
                        lineHeight: 1.35,
                        fontWeight: 600,
                        color: C.text,
                        '@media (max-width: 720px)': {fontSize: 17},
                    }}
                >
                    The CPC Index combines two complementary assessment toolkits to help countries move from measurement to action.
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
                        gap: 0,
                        border: '1px solid var(--cpc-border, rgba(0,60,105,.12))',
                    }}
                >
                    <Box
                        sx={{
                            background: C.mist,
                            boxShadow: `inset 4px 0 0 ${C.cyanDeep}`,
                            padding: '48px 40px',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRight: '1px solid var(--cpc-border, rgba(0,60,105,.12))',
                            '@media (max-width: 900px)': {
                                borderRight: 'none',
                                borderBottom: '1px solid var(--cpc-border, rgba(0,60,105,.12))',
                                padding: '36px 24px',
                            },
                        }}
                    >
                        <Box component="span" sx={badgeSx(C.cyanDeep)}>This toolkit</Box>
                        <Typography component="h3" sx={{margin: '0 0 14px', fontSize: 28, fontWeight: 700, lineHeight: 1.2, color: C.text, '@media (max-width: 720px)': {fontSize: 22}}}>
                            Government Checklist
                        </Typography>
                        <Typography sx={{margin: '0 0 16px', fontSize: 20, lineHeight: 1.3, fontWeight: 700, color: C.text, '@media (max-width: 720px)': {fontSize: 17}}}>
                            Understand how prepared your national child online protection system is.
                        </Typography>
                        <Typography sx={{margin: '0 0 14px', fontSize: 17, lineHeight: 1.45, color: C.muted, '@media (max-width: 720px)': {fontSize: 16}}}>
                            The Government Checklist evaluates national system capability across 85 standardized indicators covering laws and governance, families and communities, education, industry accountability, technology infrastructure, and public services.
                        </Typography>
                        <Typography sx={{margin: '0 0 14px', fontSize: 17, lineHeight: 1.45, color: C.muted, '@media (max-width: 720px)': {fontSize: 16}}}>
                            Review national readiness, validate the evidence behind each score, identify implementation gaps, and prioritize actions with the greatest potential impact.
                        </Typography>
                        <Box sx={{marginTop: 'auto', display: 'flex', flexWrap: 'wrap', gap: 1.25, pt: 2.25}}>
                            <Box component="span" sx={chipSx(C.cyanDeep, C.white)}>85 Government Capability Indicators</Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            background: C.surface,
                            padding: '48px 40px',
                            display: 'flex',
                            flexDirection: 'column',
                            '@media (max-width: 900px)': {padding: '36px 24px'},
                        }}
                    >
                        <Box component="span" sx={badgeSx(C.violet)}>Request access</Box>
                        <Typography component="h3" sx={{margin: '0 0 14px', fontSize: 28, fontWeight: 700, lineHeight: 1.2, color: C.text, '@media (max-width: 720px)': {fontSize: 22}}}>
                            Children's Assessment
                        </Typography>
                        <Typography sx={{margin: '0 0 16px', fontSize: 20, lineHeight: 1.3, fontWeight: 700, color: C.text, '@media (max-width: 720px)': {fontSize: 17}}}>
                            Measures children's cyber citizenship competencies and reported exposure to cyber risks.
                        </Typography>
                        <Typography sx={{margin: '0 0 14px', fontSize: 17, lineHeight: 1.45, color: C.muted, '@media (max-width: 720px)': {fontSize: 16}}}>
                            Use the assessment to understand children's readiness and cyber risk experiences, evaluate education initiatives, and monitor national outcomes over time.
                        </Typography>
                        <Box sx={{marginTop: 'auto', display: 'flex', flexWrap: 'wrap', gap: 1.25, pt: 2.25}}>
                            <Box component="span" sx={chipSx(C.mist, C.text)}>15 Cyber Citizenship Indicators</Box>
                            <Box component="span" sx={chipSx(C.mist, C.text)}>13 Global Child Cyber Risk Indicators</Box>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        margin: '24px 0 8px',
                        padding: '22px 0 0',
                        borderTop: '1px solid var(--cpc-border, rgba(0,60,105,.12))',
                    }}
                >
                    <Typography sx={{fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.cyanDeep}}>
                        Coming soon
                    </Typography>
                    <Box sx={{display: 'flex', gap: 1.25, flexWrap: 'wrap'}}>
                        <Box
                            component="span"
                            sx={{fontSize: 15, fontWeight: 700, color: C.text, background: C.mist, border: '1px solid var(--cpc-border, rgba(0,60,105,.12))', padding: '10px 16px', whiteSpace: 'nowrap'}}
                        >
                            Evidence Validation
                        </Box>
                        <Box
                            component="span"
                            sx={{fontSize: 15, fontWeight: 700, color: C.text, background: C.mist, border: '1px solid var(--cpc-border, rgba(0,60,105,.12))', padding: '10px 16px', whiteSpace: 'nowrap'}}
                        >
                            National Action Plan
                        </Box>
                        <Box
                            component="span"
                            sx={{fontSize: 15, fontWeight: 700, color: C.text, background: C.mist, border: '1px solid var(--cpc-border, rgba(0,60,105,.12))', padding: '10px 16px', whiteSpace: 'nowrap'}}
                        >
                            AI Policy Advisor
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box
                component="section"
                aria-label="How to Use the Government Toolkit"
                sx={{
                    width: '100%',
                    maxWidth: 1280,
                    margin: '0 auto',
                    padding: '88px 48px 0',
                    animation: `${cpcRise} .65s ease both`,
                    '@media (max-width: 900px)': {padding: '56px 24px 0'},
                }}
            >
                <Typography sx={{fontSize: 14, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.cyanDeep, mb: 1.75}}>
                    How to Use the Government Toolkit
                </Typography>
                <Typography
                    component="h2"
                    sx={{
                        margin: '0 0 18px',
                        fontSize: 45,
                        fontWeight: 700,
                        lineHeight: 1.15,
                        color: C.text,
                        maxWidth: '90%',
                        '@media (max-width: 900px)': {fontSize: 28, maxWidth: '100%'},
                        '@media (max-width: 720px)': {fontSize: 23},
                    }}
                >
                    5-Step Operational Workflow
                </Typography>
                <Typography
                    component="p"
                    sx={{
                        margin: '0 0 40px',
                        maxWidth: 900,
                        fontSize: 22,
                        lineHeight: 1.35,
                        fontWeight: 600,
                        color: C.text,
                        '@media (max-width: 720px)': {fontSize: 17},
                    }}
                >
                    The toolkit uses a structured, evidence-first review process.
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        border: '1px solid var(--cpc-border, rgba(0,60,105,.12))',
                        '@media (max-width: 960px)': {flexDirection: 'column'},
                    }}
                >
                    <Box sx={{flex: '1.5 1 0%', background: C.navyDeep, color: C.white, padding: '36px 40px', '@media (max-width: 720px)': {padding: '28px 20px'}}}>
                        <Box sx={{display: 'flex', gap: 2.25, padding: '20px 0', borderBottom: '1px solid rgba(255,255,255,.14)'}}>
                            <Box
                                component="span"
                                sx={{
                                    flex: 'none',
                                    width: 36,
                                    height: 36,
                                    border: `2px solid ${C.cyan}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: C.cyan,
                                    marginTop: '2px',
                                }}
                            >
                                1
                            </Box>
                            <Box>
                                <Typography sx={{fontSize: 20, fontWeight: 700, marginBottom: '6px', lineHeight: 1.25, color: C.white, '@media (max-width: 720px)': {fontSize: 17}}}>
                                    Review relevant indicators
                                </Typography>
                                <Typography sx={{fontSize: 16, lineHeight: 1.4, color: 'rgba(255,255,255,.78)', '@media (max-width: 720px)': {fontSize: 15}}}>
                                    You may review all 85 indicators or focus only on those you believe require revision and for which you can provide an informed assessment.
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{display: 'flex', gap: 2.25, padding: '20px 0', borderBottom: '1px solid rgba(255,255,255,.14)'}}>
                            <Box
                                component="span"
                                sx={{
                                    flex: 'none',
                                    width: 36,
                                    height: 36,
                                    border: `2px solid ${C.cyan}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: C.cyan,
                                    marginTop: '2px',
                                }}
                            >
                                2
                            </Box>
                            <Box>
                                <Typography sx={{fontSize: 20, fontWeight: 700, marginBottom: '6px', lineHeight: 1.25, color: C.white, '@media (max-width: 720px)': {fontSize: 17}}}>
                                    Confirm or propose a revision
                                </Typography>
                                <Typography sx={{fontSize: 16, lineHeight: 1.4, color: 'rgba(255,255,255,.78)', '@media (max-width: 720px)': {fontSize: 15}}}>
                                    Select Agree when the current assessment is accurate. Select Propose Revision when you disagree with the score, rationale, or supporting evidence.
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{display: 'flex', gap: 2.25, padding: '20px 0', borderBottom: '1px solid rgba(255,255,255,.14)'}}>
                            <Box
                                component="span"
                                sx={{
                                    flex: 'none',
                                    width: 36,
                                    height: 36,
                                    border: `2px solid ${C.cyan}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: C.cyan,
                                    marginTop: '2px',
                                }}
                            >
                                3
                            </Box>
                            <Box>
                                <Typography sx={{fontSize: 20, fontWeight: 700, marginBottom: '6px', lineHeight: 1.25, color: C.white, '@media (max-width: 720px)': {fontSize: 17}}}>
                                    Complete the structured rationale
                                </Typography>
                                <Typography sx={{fontSize: 16, lineHeight: 1.4, color: 'rgba(255,255,255,.78)', '@media (max-width: 720px)': {fontSize: 15}}}>
                                    Explain what should change, why the current assessment is inaccurate, incomplete, or outdated, and how the submitted evidence supports the proposed score.
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{display: 'flex', gap: 2.25, padding: '20px 0', borderBottom: '1px solid rgba(255,255,255,.14)'}}>
                            <Box
                                component="span"
                                sx={{
                                    flex: 'none',
                                    width: 36,
                                    height: 36,
                                    border: `2px solid ${C.cyan}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: C.cyan,
                                    marginTop: '2px',
                                }}
                            >
                                4
                            </Box>
                            <Box>
                                <Typography sx={{fontSize: 20, fontWeight: 700, marginBottom: '6px', lineHeight: 1.25, color: C.white, '@media (max-width: 720px)': {fontSize: 17}}}>
                                    Attach traceable evidence
                                </Typography>
                                <Typography sx={{fontSize: 16, lineHeight: 1.4, color: 'rgba(255,255,255,.78)', '@media (max-width: 720px)': {fontSize: 15}}}>
                                    Upload or link authoritative evidence and identify the exact page, article, section, clause, table, figure or dataset field that supports the proposal.
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{display: 'flex', gap: 2.25, padding: '20px 0'}}>
                            <Box
                                component="span"
                                sx={{
                                    flex: 'none',
                                    width: 36,
                                    height: 36,
                                    border: `2px solid ${C.cyan}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: C.cyan,
                                    marginTop: '2px',
                                }}
                            >
                                5
                            </Box>
                            <Box>
                                <Typography sx={{fontSize: 20, fontWeight: 700, marginBottom: '6px', lineHeight: 1.25, color: C.white, '@media (max-width: 720px)': {fontSize: 17}}}>
                                    Submit for CPCI validation
                                </Typography>
                                <Typography sx={{fontSize: 16, lineHeight: 1.4, color: 'rgba(255,255,255,.78)', '@media (max-width: 720px)': {fontSize: 15}}}>
                                    You may submit without reviewing every indicator. Confirm that all reviewed indicators were assessed carefully, all proposed revisions include complete evidence, and unreviewed indicators will remain unchanged. The CPCI team will validate each proposal and may accept, modify, return, or decline it.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{flex: '1 1 0%', display: 'flex', flexDirection: 'column', background: C.mist}}>
                        <Box sx={{flex: 1, padding: '36px 36px', borderBottom: '1px solid var(--cpc-border, rgba(0,60,105,.1))', '@media (max-width: 720px)': {padding: '28px 20px'}}}>
                            <Typography sx={{fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.cyanDeep, mb: 1.5}}>
                                Restricted Access
                            </Typography>
                            <Typography sx={{margin: 0, fontSize: 18, lineHeight: 1.4, fontWeight: 600, color: C.text, '@media (max-width: 720px)': {fontSize: 16}}}>
                                Available exclusively to national governments or government-designated entities.
                            </Typography>
                        </Box>
                        <Box sx={{flex: 1, padding: '36px 36px', '@media (max-width: 720px)': {padding: '28px 20px'}}}>
                            <Typography sx={{fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.cyanDeep, mb: 1.5}}>
                                Strategic Value
                            </Typography>
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.75}}>
                                <Box sx={{display: 'flex', gap: 1.5, alignItems: 'flex-start', fontSize: 18, fontWeight: 600, lineHeight: 1.3, color: C.text, '@media (max-width: 720px)': {fontSize: 16}}}>
                                    <Box component="span" sx={{width: 10, height: 10, marginTop: '7px', background: C.cyan, flexShrink: 0}}/>
                                    Inform national safety strategy
                                </Box>
                                <Box sx={{display: 'flex', gap: 1.5, alignItems: 'flex-start', fontSize: 18, fontWeight: 600, lineHeight: 1.3, color: C.text, '@media (max-width: 720px)': {fontSize: 16}}}>
                                    <Box component="span" sx={{width: 10, height: 10, marginTop: '7px', background: C.cyan, flexShrink: 0}}/>
                                    Identify systemic gaps
                                </Box>
                                <Box sx={{display: 'flex', gap: 1.5, alignItems: 'flex-start', fontSize: 18, fontWeight: 600, lineHeight: 1.3, color: C.text, '@media (max-width: 720px)': {fontSize: 16}}}>
                                    <Box component="span" sx={{width: 10, height: 10, marginTop: '7px', background: C.cyan, flexShrink: 0}}/>
                                    Design targeted interventions
                                </Box>
                                <Box sx={{display: 'flex', gap: 1.5, alignItems: 'flex-start', fontSize: 18, fontWeight: 600, lineHeight: 1.3, color: C.text, '@media (max-width: 720px)': {fontSize: 16}}}>
                                    <Box component="span" sx={{width: 10, height: 10, marginTop: '7px', background: C.cyan, flexShrink: 0}}/>
                                    Track long-term progress
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{display: 'flex', justifyContent: 'flex-end', marginTop: 6, paddingBottom: 1, '@media (max-width: 720px)': {justifyContent: 'stretch'}}}>
                    <Button type="button" onClick={goToSignIn} sx={ctaButtonSx(C.navy, C.navy, C.navyDark)}>
                        Continue to review terms →
                    </Button>
                </Box>
            </Box>
            </Box>

            <CpcFooter spaced/>
        </Box>
    );
};

export default LandingPage;
